import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../../parts/modal-new";
import SvgIcon from "../../../../../parts/SvgIcon";
import type { ClientConfigRoleLabels } from "./i18n";

interface SaveAsTemplateModalProps {
  roleLabels: ClientConfigRoleLabels;
  saveTemplateName: string;
  setSaveTemplateName: (name: string) => void;
  setShowSaveTemplateModal: (show: boolean) => void;
  handleConfirmSaveAsTemplate: () => void;
  isVisible: boolean;
  setSaveTemplateAsDefault: (value: boolean) => void;
  saveTemplateAsDefault: boolean;
  saveTemplateToCurrentClient: boolean;
  setSaveTemplateToCurrentClient: (value: boolean) => void;
}

export const SaveAsTemplateModal = ({
  roleLabels,
  saveTemplateName,
  setSaveTemplateName,
  setShowSaveTemplateModal,
  handleConfirmSaveAsTemplate,
  saveTemplateAsDefault,
  setSaveTemplateAsDefault,
  isVisible,
  saveTemplateToCurrentClient,
  setSaveTemplateToCurrentClient,
}: SaveAsTemplateModalProps) => {
  const { t } = useTranslation(["athlete-portal", "common"]);

  if (!isVisible) return null;

  const onClose = () => setShowSaveTemplateModal(false);

  const handleOverlayClose = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <Modal
      className="save-as-template-modal"
      title={t("clientConfiguration.saveAsTemplateModal.title")}
      onClose={handleOverlayClose}
    >
      <div className="body">
        <label htmlFor="save-template-name-input">
          {t("clientConfiguration.saveAsTemplateModal.templateName")}
        </label>
        <input
          id="save-template-name-input"
          type="text"
          className="input-template-title"
          placeholder={t(
            "clientConfiguration.saveAsTemplateModal.namePlaceholder"
          )}
          value={saveTemplateName}
          onChange={(e) => setSaveTemplateName(e.target.value)}
        />
      </div>
      <footer className="client-config-footer-buttons">
        <div className="left-content">
          <button
            type="button"
            onClick={() => setSaveTemplateAsDefault(!saveTemplateAsDefault)}
          >
            <SvgIcon
              src={`/img/new-icons/${
                saveTemplateAsDefault ? "" : "un"
              }checked-square.svg`}
              noFilterOnLight={saveTemplateAsDefault}
            />
            <p className="p4">
              {t("clientConfiguration.saveAsTemplateModal.setAsDefault")}
            </p>
          </button>
          <button
            type="button"
            onClick={() =>
              setSaveTemplateToCurrentClient(!saveTemplateToCurrentClient)
            }
          >
            <SvgIcon
              src={`/img/new-icons/${
                saveTemplateToCurrentClient ? "" : "un"
              }checked-square.svg`}
              noFilterOnLight={saveTemplateToCurrentClient}
            />
            <p className="p4">
              {t(
                "clientConfiguration.saveAsTemplateModal.applyToCurrentClient",
                roleLabels
              )}
            </p>
          </button>
        </div>
        <div className="right-content">
          <button type="button" data-button-type="secondary" onClick={onClose}>
            {t("common:cancel")}
          </button>
          <button
            type="button"
            data-button-type="primary"
            disabled={!saveTemplateName.trim()}
            onClick={handleConfirmSaveAsTemplate}
          >
            {t("clientConfiguration.saveAsTemplateModal.saveTemplate")}
          </button>
        </div>
      </footer>
    </Modal>
  );
};
